import type { ProductIconName } from "./productIcons";

export type MarketingProductKey =
  | "clean"
  | "stems"
  | "mastering"
  | "lyrics"
  | "artwork"
  | "motion"
  | "lyricVideo"
  | "super"
  | "enhancedAlbums"
  | "sell";

export type MarketingProduct = {
  key: MarketingProductKey;
  path: string;
  /** Name in the nav, footer and related cards. */
  name: string;
  /** Title on the overview tool cards. */
  cardName: string;
  /** Title on the related-product cards, when it differs from cardName. */
  relatedName?: string;
  category: "Prepare" | "Package" | "Sell";
  trademark?: boolean;
  icon: ProductIconName;
  /** Overview card copy, one entry per line. */
  cardLede: string[];
};

export const MARKETING_PRODUCTS: Record<MarketingProductKey, MarketingProduct> =
  {
    clean: {
      key: "clean",
      category: "Prepare",
      path: "/cleancut",
      name: "CleanCut",
      cardName: "CleanCut",
      icon: "clean",
      cardLede: [
        "No session files needed.",
        "No editing chops required.",
        "Upload an explicit track, get a clean version in minutes.",
      ],
    },
    stems: {
      key: "stems",
      category: "Prepare",
      path: "/stems",
      name: "Stems",
      cardName: "Stems",
      icon: "stems",
      cardLede: [
        "Every track you upload is automatically separated into vocals, instrumentals, drums, bass, and more, ready whenever you need them.",
      ],
    },
    mastering: {
      key: "mastering",
      category: "Prepare",
      path: "/mastering",
      name: "Mastering",
      cardName: "Mastering",
      icon: "mastering",
      cardLede: [
        "Turn your final mix into a polished, release-ready master with Lutely. Preview it, hear the difference, and move straight into your release.",
      ],
    },
    lyrics: {
      key: "lyrics",
      category: "Prepare",
      path: "/lyrics",
      name: "Lyric Transcription",
      cardName: "Lyric Transcription",
      relatedName: "Lyrics",
      icon: "lyrics",
      cardLede: [
        "Lyrics are transcribed automatically with every upload, free on every plan, then kept ready for CleanCut, translation, and lyric videos.",
      ],
    },
    artwork: {
      key: "artwork",
      category: "Prepare",
      path: "/cover-art",
      name: "Cover Art",
      cardName: "Cover Art",
      icon: "artwork",
      cardLede: [
        "Create eye-popping cover art that captures the sound, mood, and identity of your music and helps it stand out from the crowd.",
      ],
    },
    motion: {
      key: "motion",
      category: "Prepare",
      path: "/motion-art",
      name: "Motion Artwork",
      cardName: "Motion Artwork",
      icon: "video",
      cardLede: [
        "Bring your covers to life with moving visuals for Spotify Canvas and Apple Music. Preview your animation, then export the HD format you need.",
      ],
    },
    lyricVideo: {
      key: "lyricVideo",
      category: "Prepare",
      path: "/lyric-video",
      name: "Lyric Video Maker",
      cardName: "Lyric Video Maker",
      icon: "video",
      cardLede: ["Automatic with every upload. Fully customizable."],
    },
    super: {
      key: "super",
      category: "Package",
      path: "/super-single",
      name: "Super Single",
      cardName: "Super Single",
      trademark: true,
      icon: "super",
      cardLede: [
        "Turn one single into a richer fan product by including the clean version, instrumental, acapella, video, or other exclusive content, without the extra work.",
      ],
    },
    enhancedAlbums: {
      key: "enhancedAlbums",
      category: "Package",
      path: "/enhanced-album",
      name: "Enhanced Album",
      cardName: "Enhanced Album",
      icon: "enhancedAlbum",
      cardLede: [
        "Lutely automatically creates alternate versions around your album. Choose the clean, instrumental, acapella, bonus, video, or other exclusive content you want to include, then give fans one richer album experience.",
      ],
    },
    sell: {
      key: "sell",
      category: "Sell",
      path: "/sell-music-online",
      name: "Sell Music Online",
      cardName: "Sell Music Online",
      icon: "sell",
      cardLede: [
        "Sell singles, albums, and exclusive content with zero Lutely commission. Set your price and see every sale as it happens.",
      ],
    },
  };

export const LABELS_PATH = "/labels";

/** Hidden everywhere until the Enhanced Albums feature is live. */
export const ENHANCED_ALBUMS_LIVE = false;

const live = (keys: MarketingProductKey[]) =>
  keys.filter((key) => ENHANCED_ALBUMS_LIVE || key !== "enhancedAlbums");

/** Header Features menu and footer groups. */
export const PREPARE_PRODUCTS: MarketingProductKey[] = [
  "clean",
  "stems",
  "mastering",
  "lyrics",
  "artwork",
  "motion",
  "lyricVideo",
];
export const PACKAGE_PRODUCTS: MarketingProductKey[] = live([
  "super",
  "enhancedAlbums",
  "sell",
]);

/** Related-product cards at the bottom of each product page, in order. */
export const WORKFLOW_PRODUCTS: MarketingProductKey[] = live([
  "clean",
  "stems",
  "mastering",
  "lyrics",
  "artwork",
  "motion",
  "lyricVideo",
  "super",
  "enhancedAlbums",
  "sell",
]);

/** Tool cards on the /artists overview. */
export const OVERVIEW_TOOLS: MarketingProductKey[] = live([
  "clean",
  "lyrics",
  "motion",
  "mastering",
  "lyricVideo",
  "artwork",
  "stems",
  "super",
  "enhancedAlbums",
]);
